/**
 * "Account inactive" — switch an account off for a device order that was never paid.
 *
 * The chase does not do this on a timer. Blacking out every screen an account owns is visible to
 * the customer within minutes, and `forceInActive` means they cannot undo it by re-entering a
 * card — so a person decides. At the deadline the sweep opens a ticket in support@ asking for that
 * decision; this button is where it gets made.
 *
 * Shown only where it can be justified: the coupon must have been REDEEMED. An unclaimed code
 * costs us nothing and the sweep cancels it instead, so there is nothing to recover by switching
 * anyone off. The caller decides whether to render this at all (see OrderTaskTable).
 *
 * Same allowlist as the payment wall (Paul / Henry / Shawna) via
 * useOrderDeviceCanBypassPaymentGate — and, as there, this is UX only: the server enforces the
 * allowlist, the redemption rule, and re-checks Stripe regardless of what the button believes.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@staff-app/ui';
import { PowerOff } from 'lucide-react';
import type { DeviceOrderTask } from '@staff-app/shared-types';
import {
  useDeactivateAccountForDeviceOrder,
  useOrderDeviceCanBypassPaymentGate,
} from '../hooks/useOrderDeviceTasks';

export function DeactivateAccountButton({ task }: { task: DeviceOrderTask }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const { canBypass, loading: gateLoading } = useOrderDeviceCanBypassPaymentGate();
  const [deactivate, { loading, error }] = useDeactivateAccountForDeviceOrder();

  // Already off — state, not an action. Shown to everyone: it explains why an account is dark,
  // which is useful whether or not you could have done it.
  if (task.deactivatedAt) {
    return <span className="text-xs text-muted-foreground">{'Account off'}</span>;
  }

  // HIDDEN, not greyed out, for anyone who cannot press it.
  //
  // A disabled control that will never become enabled for you is noise: it advertises an action
  // as part of your job and then refuses. Only Paul, Henry and Shawna can switch an account off,
  // so for everyone else the row simply has no shutoff action.
  //
  // Except in local development, where it stays visible-but-disabled: a developer is usually not
  // on the allowlist, and hiding it would mean the control could not be seen while working on it.
  // `import.meta.env.DEV` is true only under `vite dev` — a qa or prod build has it false.
  //
  // Nothing security-relevant rests on this: the server enforces the allowlist regardless, so
  // hiding the button is tidiness, not a control.
  if (gateLoading) return null; // don't flicker a control in and then out
  if (!canBypass && !import.meta.env.DEV) return null;

  const submit = async () => {
    try {
      await deactivate({ variables: { invoiceId: task._id, reason: reason.trim() } });
      setOpen(false);
      setReason('');
    } catch {
      // Surfaced via `error` below — the server refuses on a paid invoice, and that refusal is
      // the most useful thing it can say, so the dialog stays open to show it.
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        className="gap-1"
        disabled={!canBypass}
        title={
          canBypass
            ? 'Switch this account off — every screen goes dark'
            : 'Only Paul, Henry or Shawna can switch an account off'
        }
        onClick={() => setOpen(true)}
        data-testid="order-deactivate"
      >
        <PowerOff className="h-3.5 w-3.5" />
        {'Account inactive'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{'Switch this account off?'}</DialogTitle>
            <DialogDescription>
              {'Every screen on this account goes dark, usually within minutes. The customer '}
              {'cannot restore it themselves by re-entering a card. Billing is emailed a record '}
              {'of what was done and by whom.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 text-sm">
            <div className="text-muted-foreground">
              {'Invoice '}
              <span className="font-mono">{task._id}</span>
              {task.ownerEmail ? ` · ${task.ownerEmail}` : ''}
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">
                {'Reason (recorded against the order)'}
              </span>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. PO cancelled, devices not returned"
                data-testid="order-deactivate-reason"
              />
            </label>
            {/* Stripe is re-checked on submit, so a payment made since this page loaded stops
                the shutoff rather than being discovered afterwards. */}
            <p className="text-xs text-muted-foreground">
              {'Stripe is checked again when you confirm — if the invoice has been paid, nothing '}
              {'is switched off.'}
            </p>
            {error ? (
              <p className="text-sm text-destructive" data-testid="order-deactivate-error">
                {error.message}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
              {'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => { void submit(); }}
              disabled={loading || !reason.trim()}
              data-testid="order-deactivate-confirm"
            >
              {loading ? 'Switching off…' : 'Switch off'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
